import { z } from "zod";

/**
 * Calls recorded from the lead page during a WhatsApp Desktop call (ADR 0067).
 * The browser uploads the call in parts of three tracks: `mix` (both voices, for
 * listening and for the text), `member` (the microphone) and `contact` (system
 * audio: WhatsApp plays only the lead). The single-voice tracks, their text and
 * their volume tell who said each sentence of the mix.
 */

/**
 * Parts are cut only while both voices are silent, so no word is split: from
 * CALL_PART_MIN_MS on, at a silence of CALL_SILENCE_MS; from CALL_PART_RELAXED_MS
 * on, at a shorter one; at CALL_PART_MAX_MS whatever is happening. Each part is
 * transcribed as soon as it arrives: the text follows the call by about half a
 * minute.
 */
export const CALL_PART_MIN_MS = 20_000;
export const CALL_PART_RELAXED_MS = 60_000;
export const CALL_PART_MAX_MS = 5 * 60_000;
export const CALL_SILENCE_MS = 600;
export const CALL_SILENCE_RELAXED_MS = 300;
/** Opus at 32 kbps makes about 1.2 MB in 5 minutes: anything near this is not a track. Three fit in the 25 MB Caddy lets through. */
export const CALL_PART_MAX_BYTES = 6 * 1024 * 1024;
/** A call stops after 4 hours; parts of 20 s at least fit in this many. */
export const CALL_MAX_DURATION_MS = 4 * 60 * 60_000;
export const CALL_MAX_PARTS = CALL_MAX_DURATION_MS / CALL_PART_MIN_MS;
/** One volume sample of each source every this many milliseconds. */
export const CALL_ACTIVITY_STEP_MS = 100;
/** A call still recording with no new part for this long is closed by maintenance. */
export const CALL_STALE_MS = 30 * 60_000;
/** Formats Chrome records (`MediaRecorder`) and OpenAI accepts. */
export const CALL_AUDIO_MIME_TYPES = ["audio/webm", "audio/ogg", "audio/mp4"] as const;
/** The AI reads at most this many calls of a lead, each at most this long. */
export const CALL_AI_MAX_CALLS = 5;
export const CALL_AI_MAX_CHARS = 60_000;

export const CALL_RECORDING_STATUSES = ["recording", "transcribing", "ready", "failed"] as const;
export type CallRecordingStatus = (typeof CALL_RECORDING_STATUSES)[number];

/** Why a finished call has no transcript. */
export const CALL_TRANSCRIPT_ISSUES = [
  "ai_disabled",
  "no_audio",
  "no_speech",
  "audio_rejected",
  "transcription_failed",
] as const;
export type CallTranscriptIssue = (typeof CALL_TRANSCRIPT_ISSUES)[number];

export const CALL_SPEAKERS = ["member", "contact"] as const;
export type CallSpeaker = (typeof CALL_SPEAKERS)[number];

export const CALL_TRACKS = ["mix", ...CALL_SPEAKERS] as const;
export type CallTrack = (typeof CALL_TRACKS)[number];

/** Base64 of one byte per sample: twice a part of 100 ms samples at most. */
const activityLevelsSchema = z
  .string()
  .max(16_000)
  .regex(/^[A-Za-z0-9+/]*={0,2}$/);

/** Volume of a single-voice track: 0 is -70 dBFS or less, 255 is 0 dBFS. */
export const callActivitySchema = z
  .object({
    stepMs: z.number().int().min(10).max(1_000),
    levels: activityLevelsSchema,
  })
  .strict();
export type CallActivity = z.infer<typeof callActivitySchema>;

/** The fields sent with the three tracks of a part (multipart field `meta`, JSON). */
export const callPartMetaSchema = z
  .object({
    offsetMs: z
      .number()
      .int()
      .nonnegative()
      .max(24 * 60 * 60_000),
    durationMs: z
      .number()
      .int()
      .nonnegative()
      .max(CALL_PART_MAX_MS * 2),
    /** Volume of the microphone and of the system audio; null if the browser could not measure it. */
    activity: z
      .object({ member: callActivitySchema, contact: callActivitySchema })
      .strict()
      .nullable(),
  })
  .strict();
export type CallPartMeta = z.infer<typeof callPartMetaSchema>;

/** Sent when the recording starts: the microphone Chrome uses, to explain a silent track. */
export const callStartInputSchema = z
  .object({ microphone: z.string().trim().max(200).nullable().default(null) })
  .strict();
export type CallStartInput = z.input<typeof callStartInputSchema>;

export const callFinishInputSchema = z
  .object({
    durationMs: z
      .number()
      .int()
      .nonnegative()
      .max(24 * 60 * 60_000),
    /** Parts the browser recorded; the missing ones are left out of the transcript. */
    parts: z.number().int().nonnegative().max(CALL_MAX_PARTS),
  })
  .strict();
export type CallFinishInput = z.infer<typeof callFinishInputSchema>;

export const callSegmentSchema = z
  .object({
    speaker: z.enum(CALL_SPEAKERS),
    startMs: z.number().int().nonnegative(),
    endMs: z.number().int().nonnegative(),
    text: z.string().min(1).max(20_000),
  })
  .strict();
export type CallSegment = z.infer<typeof callSegmentSchema>;

/**
 * A recorded call at a glance (ADR 0067), only from what was said: who the lead
 * is and what they want, what they need or what holds them back, what we
 * proposed, and how we left it. Empty `needs`/`proposed` when nothing was said.
 */
export const callSummaryContentSchema = z
  .object({
    brief: z.string().trim().min(1).max(600),
    needs: z.string().trim().max(600),
    proposed: z.string().trim().max(600),
    agreed: z.string().trim().min(1).max(600),
  })
  .strict();
export type CallSummaryContent = z.infer<typeof callSummaryContentSchema>;

/** What the record keeps: who wrote it last. A correction is what the AI reads afterwards. */
export const callSummarySchema = callSummaryContentSchema
  .extend({
    source: z.enum(["ai", "member"]),
    updatedAt: z.string().datetime({ offset: true }),
    editedBy: z.string().nullable().default(null),
  })
  .strict();
export type CallSummary = z.infer<typeof callSummarySchema>;

/** The words when nothing was agreed: never an invented date or deal. */
export const CALL_NO_AGREEMENT = "Nessun accordo preciso";

export type CallRecordingPartDto = {
  seq: number;
  offsetMs: number;
  durationMs: number;
  /** null once the retention removed the file: the transcript stays. */
  audioUrl: string | null;
  mimeType: string;
};

export type CallRecordingDto = {
  id: string;
  leadId: string;
  status: CallRecordingStatus;
  member: { id: string; name: string };
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  parts: CallRecordingPartDto[];
  segments: CallSegment[];
  issue: CallTranscriptIssue | null;
  transcribedAt: string | null;
  /** The summary above the transcript; null until the AI or an operator writes one. */
  summary: (CallSummaryContent & { source: "ai" | "member"; updatedAt: string }) | null;
};

/** `audio/webm;codecs=opus` → `audio/webm`, or null for a format calls do not take. */
export function callAudioMimeType(value: string | null | undefined): string | null {
  const base = value?.split(";")[0]?.trim().toLowerCase() ?? "";
  return (CALL_AUDIO_MIME_TYPES as readonly string[]).includes(base) ? base : null;
}
